// GRN phase 1 (2026-10-21), path 1 "Already bought": one screen instead of Create PO + Edit actual prices +
// Receive goods. Saving creates the PO and its receipt together (server: receiving.service createDirectPurchase).
// g03 receipt photo or "No receipt" with a reason · g05 paid by · g06 bought on · g14 price paid · g15 Goes to.
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Drawer, Input, InputNumber, Modal, Radio, Segmented, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { createDirectPurchase, PaymentMethod, ProofFile } from '../../api';
import CostCenterSelect from '../CostCenterSelect';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { daysBack, isoDay, money, outsideReceiptWindow, PAYMENT_OPTIONS, ProofPicker, REASON_AFTER_DAYS } from './shared';

export interface DirectLine {
  mrLineId: number;
  mrId: string;
  lineNo: number;
  /** Quantity still to buy on the MR line (asked). */
  asked: number;
  unit: string | null;
  /** Price to start from: the line's price or one already typed on Create PO. */
  price: number | null;
  costCenter: { id: number; code: string; name?: string | null } | null;
  item: React.ReactNode;
}

interface Props {
  open: boolean;
  vendorName: string;
  lines: DirectLine[];
  onCancel: () => void;
  onSaved: (result: { po: any; grn: any; warnings: string[] }) => void;
}

const MUTED = '#5f6672';
const label: React.CSSProperties = { fontWeight: 600, display: 'block', marginBottom: 6 };

const DirectPurchase: React.FC<Props> = ({ open, vendorName, lines, onCancel, onSaved }) => {
  const compact = useCompactLayout();
  const [boughtOn, setBoughtOn] = useState<Dayjs | null>(dayjs());
  const [reason, setReason] = useState('');
  const [payment, setPayment] = useState<PaymentMethod>('cash');
  const [proof, setProof] = useState<ProofFile[]>([]);
  const [noReceipt, setNoReceipt] = useState(false);
  const [noReceiptReason, setNoReceiptReason] = useState('');
  const [notes, setNotes] = useState('');
  const [qty, setQty] = useState<Record<number, number | null>>({});
  const [price, setPrice] = useState<Record<number, number | null>>({});
  const [cc, setCc] = useState<Record<number, number | null>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBoughtOn(dayjs()); setReason(''); setPayment('cash'); setProof([]); setNoReceipt(false); setNoReceiptReason(''); setNotes('');
    setQty(Object.fromEntries(lines.map((l) => [l.mrLineId, l.asked])));
    setPrice(Object.fromEntries(lines.map((l) => [l.mrLineId, l.price && l.price > 0 ? l.price : null])));
    setCc({});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const back = daysBack(boughtOn);
  const problems = useMemo(() => {
    const p: string[] = [];
    if (!boughtOn) p.push('Choose the date it was bought.');
    if (back > REASON_AFTER_DAYS && !reason.trim()) p.push(`Bought ${back} days ago: give a reason.`);
    if (!proof.length && !(noReceipt && noReceiptReason.trim())) p.push('Add a photo of the receipt, or tick "No receipt" and give a reason.');
    for (const l of lines) {
      const q = qty[l.mrLineId];
      if (!(q && q > 0) || q > l.asked + 1e-9) p.push(`${l.mrId} line ${l.lineNo}: bought must be more than 0 and at most ${l.asked}.`);
      if (!(price[l.mrLineId] && price[l.mrLineId]! > 0)) p.push(`${l.mrId} line ${l.lineNo}: enter the price paid.`);
      if (!l.costCenter && !cc[l.mrLineId]) p.push(`${l.mrId} line ${l.lineNo}: choose where it goes (job or store).`);
    }
    return p;
  }, [boughtOn, back, reason, proof, noReceipt, noReceiptReason, lines, qty, price, cc]);

  const total = lines.reduce((s, l) => s + (qty[l.mrLineId] || 0) * (price[l.mrLineId] || 0), 0);
  const items = lines.reduce((s, l) => s + (qty[l.mrLineId] || 0), 0);
  const partial = lines.filter((l) => (qty[l.mrLineId] || 0) > 0 && (qty[l.mrLineId] || 0) < l.asked - 1e-9);

  const save = async () => {
    if (problems.length) { message.warning(problems[0]); return; }
    setSaving(true);
    try {
      const res = await createDirectPurchase({
        boughtOn: isoDay(boughtOn),
        boughtOnReason: reason.trim() || undefined,
        paymentMethod: payment,
        proofFiles: proof,
        noProofReason: !proof.length && noReceipt ? noReceiptReason.trim() : undefined,
        notes: notes.trim() || undefined,
        lines: lines.map((l) => ({ mrLineId: l.mrLineId, qty: qty[l.mrLineId]!, unitPrice: price[l.mrLineId]!, costCenterId: l.costCenter ? undefined : cc[l.mrLineId] })),
      });
      message.success(`${res.po.poId} and ${res.grn.grnId} saved`);
      res.warnings?.forEach((w) => message.warning(w, 8));
      onSaved(res);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not save the purchase', 8);
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
      <div>
        <label style={label} htmlFor="dp-bought"><span style={{ color: '#cf1322' }}>*</span> Bought on</label>
        <DatePicker id="dp-bought" value={boughtOn} onChange={setBoughtOn} format="D MMM YYYY" allowClear={false} disabledDate={outsideReceiptWindow} style={{ width: '100%' }} inputReadOnly={compact} />
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>Up to 7 days back. A reason is asked for more than {REASON_AFTER_DAYS} days back.</div>
        {back > REASON_AFTER_DAYS && (
          <Input style={{ marginTop: 6 }} placeholder={`Why ${back} days later?`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
        )}
      </div>
      <div>
        <span style={label}>Paid by</span>
        {/* Phones: four segments cut "On account" short, so the choices wrap as plain radios. */}
        {compact ? (
          <Radio.Group options={PAYMENT_OPTIONS} value={payment} onChange={(e) => setPayment(e.target.value as PaymentMethod)} style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8 }} />
        ) : (
          <Segmented block options={PAYMENT_OPTIONS} value={payment} onChange={(v) => setPayment(v as PaymentMethod)} />
        )}
      </div>
      <div>
        <span style={label}><span style={{ color: '#cf1322' }}>*</span> Receipt photo</span>
        {!noReceipt && <ProofPicker value={proof} onChange={setProof} label="Add the receipt photo" />}
        {!proof.length && (
          <div style={{ marginTop: 6 }}>
            <Checkbox checked={noReceipt} onChange={(e) => setNoReceipt(e.target.checked)}>No receipt</Checkbox>
            {noReceipt && <Input style={{ marginTop: 6 }} placeholder="Why is there no receipt?" value={noReceiptReason} onChange={(e) => setNoReceiptReason(e.target.value)} maxLength={300} />}
          </div>
        )}
      </div>
      <div>
        <label style={label} htmlFor="dp-notes">Notes</label>
        <Input.TextArea id="dp-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </div>
    </div>
  );

  const lineRows = (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, overflow: 'hidden' }}>
      {lines.map((l, i) => {
        const value = (qty[l.mrLineId] || 0) * (price[l.mrLineId] || 0);
        return (
          <div key={l.mrLineId} style={{ padding: 12, borderTop: i ? '1px solid #f0f0f0' : undefined, display: 'grid', gap: 10, gridTemplateColumns: compact ? '1fr' : 'minmax(0, 2.2fr) 130px 140px minmax(0, 1.6fr) 110px', alignItems: 'start' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: MUTED }}>{l.mrId} · line {l.lineNo}</div>
              {l.item}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: MUTED }}>
              Bought (asked {l.asked} {l.unit || ''})
              <InputNumber min={0} max={l.asked} value={qty[l.mrLineId]} onChange={(v) => setQty((s) => ({ ...s, [l.mrLineId]: v == null ? null : Number(v) }))} inputMode="decimal" style={{ width: '100%' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: MUTED }}>
              Price paid, each
              <InputNumber min={0} precision={2} value={price[l.mrLineId]} status={price[l.mrLineId] ? undefined : 'error'} onChange={(v) => setPrice((s) => ({ ...s, [l.mrLineId]: v == null ? null : Number(v) }))} inputMode="decimal" style={{ width: '100%' }} />
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: MUTED, minWidth: 0 }}>
              Goes to
              {l.costCenter ? (
                <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.88)' }}><code>{l.costCenter.code}</code> {l.costCenter.name || ''} <span style={{ color: MUTED }}>· from the MR line</span></span>
              ) : (
                <CostCenterSelect value={cc[l.mrLineId] ?? null} onChange={(id) => setCc((s) => ({ ...s, [l.mrLineId]: id }))} placeholder="Choose job or store" status={cc[l.mrLineId] ? undefined : 'error'} />
              )}
            </div>
            <div style={{ textAlign: compact ? 'left' : 'right', fontWeight: 600, paddingTop: compact ? 0 : 18 }}>{money(value)}</div>
          </div>
        );
      })}
    </div>
  );

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {header}
      {lineRows}
      {partial.length > 0 && (
        <Alert type="info" showIcon message={`Bought less than asked on ${partial.length} line${partial.length === 1 ? '' : 's'}: the rest goes back to Create PO.`} />
      )}
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{ fontSize: 12, color: MUTED }}>{lines.length} line{lines.length === 1 ? '' : 's'} · {money(items, 0)} items · {vendorName}</div>
        <div style={{ fontSize: 17, fontWeight: 600 }}>{money(total)} EGP</div>
      </div>
      {!compact && <Button onClick={onCancel}>Back</Button>}
      <Button type="primary" loading={saving} disabled={problems.length > 0} onClick={save} style={compact ? { height: 44 } : undefined}>Save purchase</Button>
    </div>
  );
  const note = <div style={{ fontSize: 12, color: MUTED, marginTop: 6, textAlign: 'left' }}>Saves the PO (Received) and its receipt together. The price paid becomes the actual price.</div>;
  const title = `Direct purchase · ${vendorName}`;

  if (compact) {
    return (
      <Drawer open={open} onClose={onCancel} placement="right" width="100%" title={title} footer={<>{footer}{note}</>} destroyOnClose>
        {content}
      </Drawer>
    );
  }
  return (
    <Modal open={open} onCancel={onCancel} title={title} footer={<>{footer}{note}</>} width={1000} destroyOnClose maskClosable={false}>
      {content}
    </Modal>
  );
};

export default DirectPurchase;
